/* CASH REPORT — ONE ENTITY'S MONTHLY CASH PLAN, DRAWN AS ITS OWN SHEET (2026-10-25).

   Max, on the HINC sheet of the group cash forecast: "tôi đang muốn format giống nhất theo như file á. do user đã quen
   với format này rùi". So the screen draws a plan the way the workbook does: the entity's code on a dark band, "Cash Flow
   Plan — <entity>", the unit line ("Currency: USD", "(KRW 1,000)"), Forecast(year) over the months, the local label
   column where the sheet has one (HINC Korean, HJPN Korean/Japanese), the headings, lines and worked-out rows of the
   entity's format (backend cashPlanFormats.js), negatives in red brackets and a dash for nought, HINC in thousands.

   The arithmetic is evalSheet — a COPY of the server's (cashPlanFormats.js), because the form works the rows out while
   somebody types. check_cash_reports.py compares what the two print; change one, change both. */
import React from 'react';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const monthLabel = (ym) => (ym ? `${MON[Number(ym.slice(5)) - 1]}-${ym.slice(2, 4)}` : '');

/* ---- the arithmetic (keep in step with backend cashPlanFormats.js evalSheet) -------------------- */
export const sheetLines = (fmt) => (fmt?.rows || []).filter((r) => r.t === 'line');
export const evalSheet = (fmt, months, data) => {
  const amt = new Map();
  for (const l of data?.lines || []) amt.set(`${l.line_code}|${l.month}`, Number(l.amount) || 0);
  const sums = new Map((fmt?.rows || []).filter((r) => r.t === 'sum').map((r) => [r.key, r]));
  const lines = sheetLines(fmt);
  const cash = lines.filter((l) => l.dir !== 0);
  const inv = lines.filter((l) => l.group === 'inv');
  const perMonth = [];
  let open = data?.begin == null || data.begin === '' ? null : Number(data.begin);
  let iopen = data?.invBegin == null || data.invBegin === '' ? null : Number(data.invBegin);
  for (const m of months) {
    const memo = new Map();
    const val = (ref) => {
      const neg = ref.startsWith('-');
      const k = neg ? ref.slice(1) : ref;
      let v;
      if (sums.has(k)) {
        if (!memo.has(k)) memo.set(k, sums.get(k).of.reduce((s, x) => s + val(x), 0));
        v = memo.get(k);
      } else v = amt.get(`${k}|${m}`) || 0;
      return neg ? -v : v;
    };
    let net = 0; let op = 0; let fi = 0;
    for (const l of cash) {
      const v = amt.get(`${l.code}|${m}`) || 0;
      net += l.dir * v;
      if (l.cls === 'op') op += l.dir * v; else fi += l.dir * v;
    }
    const b = open; const e = b == null || Number.isNaN(b) ? null : b + net;
    const ib = iopen; const ie = ib == null || Number.isNaN(ib) ? null : ib + inv.reduce((s, l) => s + (amt.get(`${l.code}|${m}`) || 0), 0);
    perMonth.push({ month: m, val, begin: b, net, op, fi, end: e, ibegin: ib, iend: ie });
    open = e; iopen = ie;
  }
  return (fmt?.rows || []).map((r) => {
    const base = { ...r, code: r.code || r.key || null };
    if (r.t === 'line') return { ...base, values: perMonth.map((p) => (amt.has(`${r.code}|${p.month}`) ? amt.get(`${r.code}|${p.month}`) : null)) };
    if (r.t === 'sum') return { ...base, values: perMonth.map((p) => p.val(r.key)) };
    if (r.t === 'pct') return { ...base, pct: true, values: perMonth.map((p) => { const d = p.val(r.den); return d ? p.val(r.num) / d : null; }) };
    if (['begin', 'net', 'end', 'ibegin', 'iend'].includes(r.t)) return { ...base, values: perMonth.map((p) => p[r.t]) };
    return base;
  });
};

/* ---- the sheets' number format ------------------------------------------------------------------ */
/* Whole amounts, negatives in red brackets, a dash for nought; HINC (unit 'k') shown in thousands, as #,##0, does.
   A line nobody typed is EMPTY, as on the sheet; a worked-out row that comes to nothing is a dash. */
export function SheetNum({ v, unit, pct, typed, strong }) {
  if (v === null || v === undefined || Number.isNaN(v)) return typed ? null : <span className="ps-zero">-</span>;
  if (pct) return <span className="ps-pct">{`${Math.round(v * 100)}%`}</span>;
  const x = Math.round(unit === 'k' ? v / 1000 : Number(v));
  if (x === 0) return <span className="ps-zero">-</span>;
  const s = Math.abs(x).toLocaleString('en-US');
  const txt = x < 0 ? `(${s})` : s;
  return <span className={x < 0 ? 'ps-neg' : ''}>{strong ? <b>{txt}</b> : txt}</span>;
}

/* The sheet's head: the code on the dark band, the plan's name, the unit. */
export function SheetTitle({ entity, format, extra = null }) {
  const unit = format.unit === 'k' ? `(${format.currency} 1,000)` : `Currency: ${format.currency}`;
  return (
    <div className="ps-title">
      <div className="ps-band">{entity}</div>
      <div className="ps-name">{format.heading} — {format.title || entity}{extra}</div>
      <div className="ps-unit">{unit}</div>
    </div>
  );
}

/* The table: label column(s), the months under Forecast(year), Remarks. `cell(row, k)` may hand back an input for the
   form; otherwise the row's value is printed. */
export function SheetTable({ t, format, rows, months, remarks = true, cell = null, note = null }) {
  const local = !!format.localCol;
  const years = [];
  months.forEach((m) => { const y = m.slice(0, 4); const last = years[years.length - 1]; if (last && last.y === y) last.n += 1; else years.push({ y, n: 1 }); });
  const span = months.length + (local ? 2 : 1) + (remarks ? 1 : 0);
  return (
    <div className="cs-table-wrap ps-wrap">
      <table className="ps-table">
        <thead>
          <tr className="ps-years">
            <th className="ps-lab" colSpan={local ? 2 : 1} />
            {years.map((y) => <th key={y.y} colSpan={y.n} className="ps-yr">{`Forecast(${y.y})`}</th>)}
            {remarks && <th />}
          </tr>
          <tr className="ps-months">
            {local && <th className="ps-local">{format.localCol}</th>}
            <th className="ps-lab" />
            {months.map((m) => <th key={m} className="num">{monthLabel(m)}</th>)}
            {remarks && <th className="ps-rem">{t('xCashMp.h_remarks')}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            if (r.t === 'blank') return <tr key={`b${i}`} className="ps-blank"><td colSpan={span} /></tr>;
            const cls = ['ps-row', `ps-${r.t}`, r.bold ? 'ps-bold' : '', r.shade ? 'ps-shade' : '', r.yellow ? 'ps-yellow' : '', r.dir === 0 ? 'ps-memo' : ''].join(' ');
            return (
              <tr key={`${r.t}-${r.code || i}`} className={cls} data-line={r.t === 'line' ? r.code : undefined} data-row={r.t !== 'line' ? (r.code || r.t) : undefined}>
                {local && <td className="ps-local">{r.local || ''}</td>}
                <td className="ps-lab" title={r.hint || undefined}>{r.label}{!local && r.local ? <span className="cw-sub"> · {r.local}</span> : null}</td>
                {months.map((m, k) => {
                  const own = cell ? cell(r, k) : undefined;
                  return (
                    <td key={m} className={`num${own !== undefined ? ' ps-in' : ''}`}>
                      {own !== undefined ? own : (r.values ? <SheetNum v={r.values[k]} unit={format.unit} pct={r.pct} typed={r.t === 'line'} strong={r.bold} /> : null)}
                    </td>
                  );
                })}
                {remarks && <td className="ps-rem cw-sub">{r.t === 'line' ? (note ? note(r) : r.note || '') : ''}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
